import { useState } from 'react';
import { ArrowRight, CircleAlert, Loader2, MapPin, RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/workspace-dialog';
import { cn } from '@/lib/utils';
import { usePicklistStore } from '../../store/picklistStore';
import type { PicklistItemGroupDto } from '../../types';
import { ADJUST_FAILED, ADJUST_FAILED_TRANSIENT, classifyAdjustFailure } from '../../utils/adjustErrors';
import { buildMoveModel, buildMoveRequest, defaultMoveTarget, type MoveSource } from '../../utils/moveModel';

interface MoveDialogProps {
  /** The row to move from; null: closed. */
  source: MoveSource | null;
  /** Adjust's snapshot of the item group: the Move reads its limits from the same figures Adjust shows. */
  group: PicklistItemGroupDto | null;
  onClose: () => void;
  /** The Move saved: Adjust re-reads its figures and shows `text` as a toast. */
  onMoved: (text: string) => void;
  /** A 409: the figures changed under the dialog. Adjust reloads its snapshot, and this dialog re-reads its limits. */
  onStale: () => Promise<void>;
}

const MOVE_FAILED = 'Couldn’t move the units. Nothing was changed.';
const MOVE_FAILED_TRANSIENT = 'Couldn’t move the units. Nothing was changed — please try again.';

/**
 * PT-6b — "Move to another MRS line", stacked over Adjust (a nested layer, like the Post dialog's serials sheet).
 *
 * A Move is its own action, not an adjustment: nothing physical changes, only the MRS line the units count for. So it
 * asks no reason (the request carries the fixed Wrong MRS), takes an optional note, and saves the moment it is
 * confirmed — nothing is staged in Adjust (owner ruling 2026-09-30).
 */
export function MoveDialog({ source, group, onClose, onMoved, onStale }: MoveDialogProps) {
  return (
    <Dialog open={source !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        hideCloseButton
        overlayClassName="bg-black/35"
        className="flex w-[480px] max-w-[calc(100%-48px)] flex-col gap-0 overflow-hidden rounded-[14px] bg-card p-0 sm:rounded-[14px]"
      >
        {source && <MoveBody source={source} group={group} onClose={onClose} onMoved={onMoved} onStale={onStale} />}
      </DialogContent>
    </Dialog>
  );
}

function MoveBody({ source, group, onClose, onMoved, onStale }: MoveDialogProps & { source: MoveSource }) {
  // The one line with room is preselected; the quantity shows its default until the person types.
  const [to, setTo] = useState<number | null>(() => (group ? defaultMoveTarget(group, source) : null));
  const [n, setN] = useState<string | undefined>(undefined);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);

  const model = group ? buildMoveModel(group, source, { to, n }) : null;

  const submit = async () => {
    if (!group || !model) return;
    const request = buildMoveRequest(model, notes, group.adjustVersion);
    if (!request) return;
    setSubmitting(true);
    setError(null);
    try {
      await usePicklistStore.getState().adjustItem(group.id, request);
      onMoved(model.doneText);
    } catch (failure) {
      const outcome = classifyAdjustFailure(failure);
      if (outcome.kind === 'stale') {
        // The figures changed: nothing was moved. Show why, then re-read the limits from the reloaded snapshot.
        setStale(true);
        setN(undefined);
        await onStale();
      } else {
        // The classifier's generic lines name "the adjustment"; a Move says its own.
        setError(outcome.text === ADJUST_FAILED_TRANSIENT ? MOVE_FAILED_TRANSIENT : outcome.text === ADJUST_FAILED ? MOVE_FAILED : outcome.text);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="flex items-start justify-between gap-3 px-[18px] pt-4">
        <div className="min-w-0">
          <DialogTitle className="text-[15px] font-bold">Move to another MRS line</DialogTitle>
          <DialogDescription asChild>
            <div className="mt-[5px] flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
              <span className="font-mono text-xs font-semibold text-foreground">{model?.code ?? group?.itemCode ?? ''}</span>
              {model && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3 w-3" aria-hidden />
                  <span className="font-mono">{model.bin}</span>
                </span>
              )}
              {model?.serial && <span className="font-mono text-foreground">{model.serial}</span>}
            </div>
          </DialogDescription>
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[7px] text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </TooltipTrigger>
          <TooltipContent>Close</TooltipContent>
        </Tooltip>
      </div>

      {!model ? (
        <div className="px-[18px] py-4">
          <EmptyState size="compact" title="This pick is no longer here — the picklist changed." />
        </div>
      ) : (
        <div className="flex flex-col gap-3 px-[18px] pb-1 pt-3">
          {stale && (
            <div role="alert" className="flex items-start gap-2 rounded-lg border border-warning/35 bg-warning/[0.08] px-2.5 py-2">
              <RefreshCw className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" aria-hidden />
              <span className="text-xs leading-[1.45] text-foreground">
                <b className="font-semibold">Nothing was moved — the figures changed.</b> Someone changed this item while you had
                it open. The limits below are the new ones.
              </span>
            </div>
          )}

          <p className="text-xs leading-normal text-muted-foreground">
            The units stay picked, in the same bin. Only the MRS line they count for changes.
          </p>

          <div className="rounded-[10px] border border-border">
            <div className="flex items-start gap-2.5 px-3 py-2.5">
              <span className="w-10 shrink-0 pt-px text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">From</span>
              <div className="min-w-0 flex-1">
                <div className="break-words text-xs font-medium">{model.fromLabel}</div>
                <div className="mt-0.5 text-[11.5px] tabular-nums text-muted-foreground">{model.availableLabel}</div>
              </div>
            </div>
            <div className="flex items-center gap-2.5 border-t border-border/60 px-3 py-2.5">
              <span className="flex w-10 shrink-0 items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
                To <ArrowRight className="h-3 w-3" aria-hidden />
              </span>
              <Select value={model.to != null ? String(model.to) : ''} onValueChange={(v) => { setTo(v ? Number(v) : null); setN(undefined); }}>
                <SelectTrigger aria-label="MRS line to count them for" className="h-8 min-w-0 flex-1 rounded-[7px] text-xs">
                  <SelectValue placeholder="Choose an MRS line…" />
                </SelectTrigger>
                <SelectContent>
                  {model.targets.map((t) => (
                    <SelectItem key={t.lineId} value={String(t.lineId)} disabled={t.disabled} className="text-xs">
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2.5 border-t border-border/60 px-3 py-2.5">
              <label htmlFor="move-quantity" className="w-10 shrink-0 text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
                Qty
              </label>
              {model.isSerial ? (
                <span className="text-xs">1 unit (serial)</span>
              ) : (
                <>
                  <Input
                    id="move-quantity"
                    type="number"
                    min={0}
                    max={model.max}
                    value={model.quantityText}
                    onChange={(e) => setN(e.target.value)}
                    aria-invalid={model.quantityInvalid}
                    className={cn('h-8 w-24 rounded-[7px] px-2.5 text-right text-[13px] font-medium md:text-[13px]', model.quantityInvalid && 'border-destructive')}
                  />
                  <span className="text-xs text-muted-foreground">{model.unit}</span>
                </>
              )}
            </div>
          </div>

          <div className={cn('text-[11.5px] leading-[1.45]', model.noteIsError ? 'text-destructive' : 'text-muted-foreground')} role={model.noteIsError ? 'alert' : undefined}>
            {model.note}
          </div>

          <div>
            <label htmlFor="move-notes" className="mb-1.5 block text-xs font-semibold">
              Note <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            <div className="relative">
              <textarea
                id="move-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value.slice(0, 500))}
                rows={2}
                maxLength={500}
                placeholder="Anything that explains the move"
                className="block w-full resize-y rounded-lg border border-input bg-background px-[11px] pb-4 pt-2 text-[12.5px] text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
              {notes && (
                <span className="pointer-events-none absolute bottom-1 right-2.5 text-[10px] tabular-nums text-muted-foreground">
                  {notes.length} / 500
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mt-3 flex items-center justify-end gap-2 border-t border-border px-[18px] pb-3.5 pt-3">
        {error && (
          <div role="alert" className="mr-auto flex min-w-0 items-start gap-1.5 text-xs leading-[1.4] text-destructive">
            <CircleAlert className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>{error}</span>
          </div>
        )}
        <Button variant="outline" className="h-9 text-[13px]" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          className="h-9 gap-1.5 text-[13px] font-semibold"
          disabled={!model?.canMove || submitting || !group?.adjustVersion}
          title={model?.moveTip || undefined}
          onClick={() => void submit()}
        >
          {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden />}
          Move
        </Button>
      </div>
    </>
  );
}
